import { useState, FormEvent } from 'react';

export function AsIsVsRemodelFinalCTA() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    propertyAddress: '',
    units: '',
    condition: '',
    timeline: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d5qq6votu5df1co4dbgg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          propertyAddress: '',
          units: '',
          condition: '',
          timeline: '',
          message: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="py-20 bg-gradient-to-br from-teal-600 to-teal-700">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16 text-white">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Ready to Sell Your Apartment Building AS IS?
          </h2>
          <p className="text-xl md:text-2xl max-w-3xl mx-auto leading-relaxed">
            Skip the renovation headaches, avoid the massive costs, and get cash in hand fast. Get your fair AS IS offer in 24-48 hours.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-start">
          {/* Benefits */}
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-10 border-2 border-white/20">
            <h3 className="text-3xl font-bold mb-8 text-white">Why Sell AS IS to Us?</h3>
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-money-dollar-circle-line text-2xl text-teal-600"></i>
                </div>
                <div className="text-white">
                  <h4 className="text-xl font-bold mb-2">Save $500K-$2M+</h4>
                  <p className="text-white/90">Avoid all renovation and carrying costs while still getting a fair price</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-time-line text-2xl text-teal-600"></i>
                </div>
                <div className="text-white">
                  <h4 className="text-xl font-bold mb-2">Close in 7-30 Days</h4>
                  <p className="text-white/90">Get cash fast instead of waiting 12-18 months for renovations</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-shield-check-line text-2xl text-teal-600"></i>
                </div>
                <div className="text-white">
                  <h4 className="text-xl font-bold mb-2">Zero Stress or Risk</h4>
                  <p className="text-white/90">No contractors, no permits, no surprises—we handle everything</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-hand-coin-line text-2xl text-teal-600"></i>
                </div>
                <div className="text-white">
                  <h4 className="text-xl font-bold mb-2">All Cash Offer</h4>
                  <p className="text-white/90">No financing contingencies or buyer loan delays</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-tools-line text-2xl text-teal-600"></i>
                </div>
                <div className="text-white">
                  <h4 className="text-xl font-bold mb-2">Any Condition Accepted</h4>
                  <p className="text-white/90">We buy properties with major repairs, deferred maintenance, and problem tenants</p>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-white rounded-2xl p-10 shadow-2xl" id="contact-form">
            <h3 className="text-3xl font-bold mb-6">Get Your Free AS IS Cash Offer</h3>
            <p className="text-gray-700 mb-8">Fill out the form below and we'll contact you within 24 hours with a fair, no-obligation offer.</p>
            
            <form onSubmit={handleSubmit} data-readdy-form className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-sm font-bold text-gray-700 mb-2">
                  Full Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-teal-600 focus:outline-none text-base"
                  placeholder="John Smith"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="email" className="block text-sm font-bold text-gray-700 mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-teal-600 focus:outline-none text-base"
                    placeholder="john@example.com"
                  />
                </div>
                <div>
                  <label htmlFor="phone" className="block text-sm font-bold text-gray-700 mb-2">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    required
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-teal-600 focus:outline-none text-base"
                    placeholder="(555) 123-4567"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="propertyAddress" className="block text-sm font-bold text-gray-700 mb-2">
                  Property Address *
                </label>
                <input
                  type="text"
                  id="propertyAddress"
                  name="propertyAddress"
                  required
                  value={formData.propertyAddress}
                  onChange={(e) => setFormData({ ...formData, propertyAddress: e.target.value })}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-teal-600 focus:outline-none text-base"
                  placeholder="123 Main St, City, State, ZIP"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="units" className="block text-sm font-bold text-gray-700 mb-2">
                    Number of Units *
                  </label>
                  <input
                    type="number"
                    id="units"
                    name="units"
                    required
                    value={formData.units}
                    onChange={(e) => setFormData({ ...formData, units: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-teal-600 focus:outline-none text-base"
                    placeholder="20"
                  />
                </div>
                <div>
                  <label htmlFor="condition" className="block text-sm font-bold text-gray-700 mb-2">
                    Property Condition *
                  </label>
                  <select
                    id="condition"
                    name="condition"
                    required
                    value={formData.condition}
                    onChange={(e) => setFormData({ ...formData, condition: e.target.value })}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-teal-600 focus:outline-none text-base"
                  >
                    <option value="">Select condition</option>
                    <option value="excellent">Excellent</option>
                    <option value="good">Good</option>
                    <option value="fair">Fair</option>
                    <option value="needs-work">Needs Work</option>
                    <option value="major-repairs">Major Repairs Needed</option>
                  </select>
                </div>
              </div>

              <div>
                <label htmlFor="timeline" className="block text-sm font-bold text-gray-700 mb-2">
                  When Do You Want to Sell? *
                </label>
                <select
                  id="timeline"
                  name="timeline"
                  required
                  value={formData.timeline}
                  onChange={(e) => setFormData({ ...formData, timeline: e.target.value })}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-teal-600 focus:outline-none text-base"
                >
                  <option value="">Select timeline</option>
                  <option value="asap">As Soon As Possible</option>
                  <option value="1-month">Within 1 Month</option>
                  <option value="1-3-months">1-3 Months</option>
                  <option value="3-6-months">3-6 Months</option>
                  <option value="just-exploring">Just Exploring Options</option>
                </select>
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-bold text-gray-700 mb-2">
                  Additional Details (Optional)
                </label>
                <textarea
                  id="message"
                  name="message"
                  rows={4}
                  maxLength={500}
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-teal-600 focus:outline-none text-base resize-none"
                  placeholder="Tell us about your property, situation, or any questions you have... (500 characters max)"
                ></textarea>
                <div className="text-sm text-gray-500 mt-1 text-right">
                  {formData.message.length}/500 characters
                </div>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full px-8 py-4 bg-teal-600 text-white rounded-xl font-bold text-lg hover:bg-teal-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed whitespace-nowrap"
              >
                {isSubmitting ? 'Submitting...' : 'Get My Free Cash Offer'}
              </button>

              {submitStatus === 'success' && (
                <div className="bg-green-50 border-2 border-green-500 rounded-lg p-4 text-center">
                  <p className="text-green-700 font-bold">Thank you! We'll contact you within 24 hours with your offer.</p>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="bg-red-50 border-2 border-red-500 rounded-lg p-4 text-center">
                  <p className="text-red-700 font-bold">Something went wrong. Please try again or call us directly.</p>
                </div>
              )}

              <p className="text-sm text-gray-600 text-center">
                By submitting this form, you agree to be contacted about your property. We respect your privacy and never share your information.
              </p>
            </form>
          </div>
        </div>

        {/* Final Image */}
        <div className="mt-16 rounded-2xl overflow-hidden shadow-2xl">
          <img
            src="https://public.readdy.ai/ai/img_res/dc891d5ae9b0631aa345e70b87684d5d.jpg"
            alt="Successful apartment building AS IS sale"
            className="w-full h-[400px] object-cover object-top"
          />
        </div>
      </div>
    </section>
  );
}